import UseCasePage, {
  type UseCaseFaq,
  type UseCaseScene,
  type UseCaseStat,
  type UseCaseWhy,
} from "@/components/public/use-case-page"
import type { SeoMeta } from "@/types"

const WHY: UseCaseWhy[] = [
  {
    title: "The first 48 hours drive 70% of portal interest",
    description: "Most viewing requests come in the first two days of a listing going live. A just-listed video ad captures that spike on social, while the Rightmove alert email is still fresh in inboxes.",
  },
  {
    title: "Instagram pushes new content harder than old",
    description: "Reels and TikTok reward fresh uploads in the first 24 hours. Posting a just-listed video the moment the instruction goes live compounds portal reach with organic social reach \u2014 for free.",
  },
  {
    title: "It signals momentum to prospective vendors",
    description: "Every just-listed video that shows up in a local area builds the impression of a busy, active agent. Future instruction wins start with prospective vendors seeing your name again and again.",
  },
  {
    title: "Automating removes the \"we forgot to post\" problem",
    description: "Every agency says they want to post video for new listings. Almost none do it consistently. QuiteRight's auto-publish rules mean every listing gets one within minutes of going live \u2014 no chaser, no negotiator to nag.",
  },
]

const SCENES: UseCaseScene[] = [
  { title: "\"Just listed\" title card", duration: "0:00\u20130:02", description: "Bold headline with address and \u201Cnew instruction\u201D label. Animated in with your agency colours and logo so the brand lands before the content." },
  { title: "Hero shot of the exterior", duration: "0:02\u20130:05", description: "Best external photo with a slow Ken Burns zoom. Caption drops in the price and bedroom count as soon as the image lands." },
  { title: "Three standout interior frames", duration: "0:05\u20130:15", description: "Kitchen, living room, master bedroom. Each held for 3\u20134 seconds with a short on-screen feature caption \u2014 e.g. \u201CShaker kitchen with island\u201D \u2014 written from the listing details." },
  { title: "Key facts snap", duration: "0:15\u20130:22", description: "Bedrooms, bathrooms, EPC band, square footage if available. Static frame with clean typography \u2014 easy to scroll-pause-and-read on the phone." },
  { title: "Call to action", duration: "0:22\u20130:30", description: "\u201CBook a viewing\u201D CTA with your office phone number and a prompt to DM. The voiceover tees up the viewing slot directly." },
]

const STATS: UseCaseStat[] = [
  { value: "<60s", label: "from URL to post", detail: "Paste the Rightmove / Zoopla URL when you push live, have the ad in feed minutes later." },
  { value: "9:16", label: "+ 1:1 + 16:9", detail: "Every render ships three aspect ratios. Reels, feed, YouTube \u2014 no re-exporting." },
  { value: "Auto", label: "publish on create", detail: "Set an auto-publish rule per account: every new listing posts itself on day one." },
  { value: "\u221E", label: "listings per month", detail: "Unlimited rendering on the \u00A3499/mo plan. Every instruction gets a just-listed ad." },
]

const FAQ: UseCaseFaq[] = [
  {
    q: "How soon after listing should the video go live?",
    a: "Ideally within two hours of the listing going live on Rightmove. Portal alerts and your own social both peak on day one \u2014 a matching video in feed compounds the impression. With QuiteRight's auto-publish rule, you can make this automatic.",
  },
  {
    q: "What length should a just-listed video be?",
    a: "20\u201330 seconds. Long enough to show the exterior, 3 strong interiors, and the key facts. Short enough that the average scroll watches it through and at least sees the CTA. QuiteRight's just-listed template targets this length by default.",
  },
  {
    q: "Do I need professional photos?",
    a: "Not to start. The video works with whatever is on the portal listing. For higher-end instructions, pairing QuiteRight with proper photography (BoxBrownie, in-house photographer) produces noticeably stronger results.",
  },
  {
    q: "Which platform should I post to first?",
    a: "Instagram Reels usually performs best for UK property in the just-listed window, followed by Facebook (older demographic, warm buyers), then TikTok for younger audiences. QuiteRight publishes to all three at once.",
  },
  {
    q: "Can the voiceover read the actual listing copy?",
    a: "No \u2014 and it shouldn't. Portal copy is long and dense. QuiteRight's script generator writes a short, social-first voiceover from the listing data: headline, three features, call to action. It reads well out loud at 20\u201330 seconds.",
  },
]

export default function JustListedVideo({ meta }: { meta: SeoMeta }) {
  return (
    <UseCasePage
      meta={meta}
      useCaseName="Just Listed Video"
      breadcrumbName="Just Listed Video"
      heroHeadline="A just-listed video in feed before the portal email is even read"
      heroSubhead="The first 48 hours of a new instruction drive most of the portal traffic. Pair it with a just-listed video ad on Instagram and Facebook, posted within minutes of going live, to compound reach and signal momentum."
      whyHeadline="Why just-listed videos outperform a static post"
      whySubhead="Portal demand peaks on day one. Social algorithms favour fresh content. A just-listed video stacks both moments \u2014 for free."
      whyItems={WHY}
      anatomyHeadline="Anatomy of a just-listed video"
      anatomySubhead="A 25\u201330 second structure QuiteRight renders for every new instruction, with your agency branding auto-applied."
      scenes={SCENES}
      stats={STATS}
      faq={FAQ}
      ctaHeadline="Never miss a just-listed moment again"
      ctaBody="Every new instruction, a just-listed video ad on every social channel \u2014 automatically, on the day it goes live."
      relatedKey="use-cases/just-listed-video"
    />
  )
}
